import { useSearchParams, useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { FaUserFriends, FaSuitcaseRolling, FaSearch } from "react-icons/fa";
import { GiCarDoor } from "react-icons/gi";
import { MdFilterList } from "react-icons/md";
import { IoSnowOutline } from "react-icons/io5";
import aombre from "../images/logo-dark.png";
import logo from "../images/Aombre-round-logo.png";
import { FiX } from "react-icons/fi";
import nodata from "../images/nodata-img.webp";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateTimeError, setDateTimeError] = useState("");


  const [mobileEditMode, setMobileEditMode] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formData = useMemo(
    () => ({
      pickup: {
        pickup_date: searchParams.get("pickup_date") || "",
        pickup_location: searchParams.get("pickup_location") || "",
        pickup_airport_code: searchParams.get("pickup_airport_code") || "",
        pickup_city: searchParams.get("pickup_city") || "",
        pickup_country: searchParams.get("pickup_country") || "",
        pickup_airport_name: searchParams.get("pickup_airport_name") || "",
      },
      dropoff: {
        dropoff_date: searchParams.get("dropoff_date") || "",
        dropoff_location: searchParams.get("dropoff_location") || "",
        dropoff_airport_code: searchParams.get("dropoff_airport_code") || "",
        dropoff_city: searchParams.get("dropoff_city") || "",
        dropoff_country: searchParams.get("dropoff_country") || "",
        dropoff_airport_name: searchParams.get("dropoff_airport_name") || "",
      },
      same_location: searchParams.get("same_location") === "true",
      promo_code: searchParams.get("promo_code") || "",
    }),
    [searchParams]
  );

  function parseLocalDateTime(dateTimeStr) {
    if (!dateTimeStr) return null;
    const [datePart, timePart] = dateTimeStr.split("T");
    if (!datePart || !timePart) return null;

    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);

    return new Date(year, month - 1, day, hour, minute);
  }

  const [pickupQuery, setPickupQuery] = useState(
    formData.pickup.pickup_location
  );
  const [dropoffQuery, setDropoffQuery] = useState(
    formData.dropoff.dropoff_location
  );
  const [pickupDate, setPickupDate] = useState(
    parseLocalDateTime(formData.pickup.pickup_date)
  );
  const [dropoffDate, setDropoffDate] = useState(
    parseLocalDateTime(formData.dropoff.dropoff_date)
  );

  const [pickupLocationObj, setPickupLocationObj] = useState(null);
  const [dropoffLocationObj, setDropoffLocationObj] = useState(null);
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  function formatLocalDateTime(date) {
    if (!date) return "";
    const pad = (num) => String(num).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hour = pad(date.getHours());
    const minute = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  const [originalVehicles, setOriginalVehicles] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    vehicleFilters: {
      cars: { checked: false, subTypes: [] },
      suvs: { checked: false, subTypes: [] },
      "vans-trucks": { checked: false, subTypes: [] },
    },
    mileage: [],
    passengerFilters: {
      "4+": { checked: false, vehicleTypes: [] },
      "5+": { checked: false, vehicleTypes: [] },
      "6+": { checked: false, vehicleTypes: [] },
      "7+": { checked: false, vehicleTypes: [] },
    },
    maxPrice: "",
  });

  // const [sortOption, setSortOption] = useState("");
  const [sortOption, setSortOption] = useState("low-to-high");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Session expiration logic
  useEffect(() => {
    let timeoutId;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSessionExpired(true);
      }, 90000);
    };

    const handleActivity = () => {
      if (!sessionExpired) {
        resetTimer();
      }
    };

    // Initial timer start
    resetTimer();

    // Event listeners for user activity
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("keydown", handleActivity);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, [sessionExpired]);

  useEffect(() => {
    const fetchVehicleData = async () => {
      if (
        !formData.pickup.pickup_airport_code ||
        !formData.dropoff.dropoff_airport_code
      ) {
        setError("Missing required location data");
        setLoading(false);
        return;
      }

      const payload = {
        account_id: "690337",
        pickup_location_id: `690337-${formData.pickup.pickup_airport_code}`,
        dropoff_location_id: `690337-${formData.dropoff.dropoff_airport_code}`,
        pickup: pickupDate ? `${formatLocalDateTime(pickupDate)}:00.000` : "",
        dropoff: dropoffDate
          ? `${formatLocalDateTime(dropoffDate)}:00.000`
          : "",
        ota_code: "329836",
        rate_code: "",
        driver_country: formData.pickup.pickup_country,
        pickup_country: formData.pickup.pickup_country,
        pickup_time: pickupDate
          ? formatLocalDateTime(pickupDate).split("T")[1]
          : "12:00",
        dropoff_time: dropoffDate
          ? formatLocalDateTime(dropoffDate).split("T")[1]
          : "12:00",
        promo_code: formData.promo_code,
      };

      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const res = await axios.post(
          `${baseUrl}get_rate_routes_new_website`,
          payload
        );
        console.log("API RESPONSE:", res.data);

        if (res.data && Object.keys(res.data).length > 0) {
          setRates(res.data);
          const vehicles = getVehiclesArray(res.data);
          setOriginalVehicles(vehicles);
          setFilteredData(vehicles);
        } else {
          setOriginalVehicles([]);
          setFilteredData([]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
        setError("Failed to load vehicle data");
        setLoading(false);
      }
    };

    fetchVehicleData();
  }, [formData]);

  function normalizeCarType(type) {
    if (!type) return "";
    const lower = type.toLowerCase().replace(/['"]/g, "");
    if (lower.includes("suv")) return "suvs";
    if (lower.includes("car")) return "cars";
    if (lower.includes("truck")) return "trucks";
    if (lower.includes("van")) return "vans";
    if (lower.includes("minivan")) return "minivans";
    return lower;
  }

  function getVehiclesArray(ratesData) {
    if (!ratesData) return [];

    const allVehicles = [];

    Object.keys(ratesData).forEach((paymentType) => {
      const vehiclesGroup = ratesData[paymentType];
      if (!vehiclesGroup || typeof vehiclesGroup !== "object") return;

      Object.entries(vehiclesGroup).forEach(([code, details], idx) => {
        const car = details.car_detail || {};
        const lor = ratesData.length_of_rental || 1;

        let taxValue = details.tax_details
          ? Object.values(details.tax_details).reduce((sum, tax) => {
            return sum + (parseFloat(tax.value) || 0);
          }, 0)
          : 0;

        allVehicles.push({
          id: `${paymentType}-${idx + 1}`,
          paymentType,
          image: car.image_path
            ? `https://backend.aombrerental.com/static/fleet_img/${car.image_path}`
            : "",
          name: car.make || "Unknown",
          model: car.model || code,
          description: car.description || code,
          currency: details.currency || "USD",
          base_rate: details.base_rate || 0,
          total_price: details.price_after_taxes || 0,
          tax_value: taxValue,
          tax_details: details.tax_details || {},
          price: details.price_after_taxes / lor,
          lor,
          bags: car.luggage_count || 0,
          door: car.door_count || 0,
          seats: car.seat_count || 0,
          carType: normalizeCarType(car.car_main_class),
          mileage: car.mileage || "",
          ac: car.car_ac || "N/A",
          vehicleSubType: car.car_class || "",
          sipp_code: car.SIPP_code,
          rateDetails: details,
        });
      });
    });

    return allVehicles;
  }

  const applyFiltersAndSort = useCallback(
    (data) => {
      let result = [...data];

      const activeVehicleTypes = Object.keys(filters.vehicleFilters).filter(
        (type) => filters.vehicleFilters[type].checked
      );
      if (activeVehicleTypes.length > 0) {
        result = result.filter((car) =>
          activeVehicleTypes.includes(car.carType)
        );
      }

      const activeSubTypes = [];
      Object.keys(filters.vehicleFilters).forEach((type) => {
        if (filters.vehicleFilters[type].checked) {
          activeSubTypes.push(...filters.vehicleFilters[type].subTypes);
        }
      });
      if (activeSubTypes.length > 0) {
        result = result.filter((car) =>
          activeSubTypes.includes(car.vehicleSubType)
        );
      }

      if (filters.mileage.length > 0) {
        result = result.filter((car) => {
          const mileageType = car.mileage ? "Limited" : "Unlimited";
          return filters.mileage.includes(mileageType);
        });
      }

      const activePassengerFilters = Object.keys(
        filters.passengerFilters
      ).filter((passenger) => filters.passengerFilters[passenger].checked);
      if (activePassengerFilters.length > 0) {
        result = result.filter((car) => {
          const seatCount = parseInt(car.seats);
          return activePassengerFilters.some((filter) => {
            const minSeats = parseInt(filter.replace("+", ""));
            return seatCount >= minSeats;
          });
        });
      }

      if (filters.maxPrice) {
        result = result.filter((car) => car.price <= filters.maxPrice);
      }

      if (sortOption === "low-to-high") {
        result.sort((a, b) => a.price - b.price);
      } else if (sortOption === "high-to-low") {
        result.sort((a, b) => b.price - a.price);
      } else if (sortOption === "name-asc") {
        result.sort((a, b) => a.name.localeCompare(b.name));
      } else if (sortOption === "name-desc") {
        result.sort((a, b) => b.name.localeCompare(a.name));
      }

      return result;
    },
    [filters, sortOption]
  );

  useEffect(() => {
    if (originalVehicles.length > 0) {
      setFilteredData(applyFiltersAndSort(originalVehicles));
    }
  }, [filters, sortOption, originalVehicles, applyFiltersAndSort]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showPickupSuggestions && !e.target.closest(".w-full.relative")) {
        setShowPickupSuggestions(false);
      }
      if (showDropoffSuggestions && !e.target.closest(".w-full.relative")) {
        setShowDropoffSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPickupSuggestions, showDropoffSuggestions]);

  const fetchLocations = async (query, setSuggestions) => {
    if (!query) return setSuggestions([]);
    try {
      const res = await axios.get(
        `https://backend.aombrerental.com/api/get_country_city_airport_name`
      );
      const filtered = res.data.filter((loc) =>
        `${loc.city} ${loc.airport_name} ${loc.country_name} ${loc.airport_code} ${loc.city_name}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
      setSuggestions(filtered);
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
    }
  };

  const debouncedFetchPickup = useCallback(
    debounce((query) => fetchLocations(query, setPickupSuggestions), 300),
    []
  );

  const debouncedFetchDropoff = useCallback(
    debounce((query) => fetchLocations(query, setDropoffSuggestions), 300),
    []
  );

  const handlePickupInput = (e) => {
    const value = e.target.value;
    setPickupQuery(value);
    setShowPickupSuggestions(true);
    debouncedFetchPickup(value);
  };

  const handleDropoffInput = (e) => {
    const value = e.target.value;
    setDropoffQuery(value);
    setShowDropoffSuggestions(true);
    debouncedFetchDropoff(value);
  };

  const handleSelectPickup = (loc) => {
    const locationString = `${loc.city} - ${loc.airport_name} (${loc.airport_code}) - ${loc.country_name}`;
    setPickupQuery(locationString);
    setPickupLocationObj(loc);
    setShowPickupSuggestions(false);
  };

  const handleSelectDropoff = (loc) => {
    const locationString = `${loc.city} - ${loc.airport_name} (${loc.airport_code}) - ${loc.country_name}`;
    setDropoffQuery(locationString);
    setDropoffLocationObj(loc);
    setShowDropoffSuggestions(false);
  };

  const triggerPopup = (message) => {
    setPopupMessage(message);
    setShowPopup(true);

    setTimeout(() => {
      setShowPopup(false);
    }, 3000);
  };

  const isFormValid = () => {
    if (!pickupQuery || pickupQuery.trim() === "") {
      triggerPopup("Please enter a valid pickup location");
      return false;
    }
    if (!dropoffQuery || dropoffQuery.trim() === "") {
      triggerPopup("Please enter a valid dropoff location");
      return false;
    }
    if (!pickupDate) {
      triggerPopup("Please select a pickup date and time");
      return false;
    }
    if (!dropoffDate) {
      triggerPopup("Please select a dropoff date and time");
      return false;
    }
    if (new Date(dropoffDate) <= new Date(pickupDate)) {
      triggerPopup("Dropoff date & time must be later than pickup.");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    let finalPickupLocation = pickupLocationObj;
    let finalDropoffLocation = dropoffLocationObj;

    if (!finalPickupLocation && pickupQuery.trim() !== "") {
      finalPickupLocation = {
        airport_code: formData.pickup.pickup_airport_code,
        city: formData.pickup.pickup_city,
        country_name: formData.pickup.pickup_country,
        airport_name: formData.pickup.pickup_airport_name,
      };
    }

    if (!finalDropoffLocation && dropoffQuery.trim() !== "") {
      finalDropoffLocation = {
        airport_code: formData.dropoff.dropoff_airport_code,
        city: formData.dropoff.dropoff_city,
        country_name: formData.dropoff.dropoff_country,
        airport_name: formData.dropoff.dropoff_airport_name,
      };
    }

    if (!finalPickupLocation || !finalDropoffLocation) {
      triggerPopup(
        "Please select valid pickup and dropoff locations from the suggestions"
      );
      return;
    }

    const newSearchParams = new URLSearchParams();

    newSearchParams.set("pickup_location", pickupQuery);
    newSearchParams.set(
      "pickup_airport_code",
      finalPickupLocation.airport_code
    );
    newSearchParams.set("pickup_city", finalPickupLocation.city);
    newSearchParams.set("pickup_country", finalPickupLocation.country_name);
    newSearchParams.set(
      "pickup_airport_name",
      finalPickupLocation.airport_name
    );
    newSearchParams.set(
      "pickup_date",
      pickupDate ? formatLocalDateTime(pickupDate) : ""
    );

    newSearchParams.set("dropoff_location", dropoffQuery);
    newSearchParams.set(
      "dropoff_airport_code",
      finalDropoffLocation.airport_code
    );
    newSearchParams.set("dropoff_city", finalDropoffLocation.city);
    newSearchParams.set("dropoff_country", finalDropoffLocation.country_name);
    newSearchParams.set(
      "dropoff_airport_name",
      finalDropoffLocation.airport_name
    );
    newSearchParams.set(
      "dropoff_date",
      dropoffDate ? formatLocalDateTime(dropoffDate) : ""
    );

    newSearchParams.set(
      "same_location",
      (
        finalPickupLocation.airport_code === finalDropoffLocation.airport_code
      ).toString()
    );

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${newSearchParams.toString()}`
    );
    window.location.reload();
  };

  const handleChooseCar = (car) => {
    if (!isFormValid()) {
      return;
    }
    setSelectedCar(car);
  };

  const handleBookNow = (car) => {
    if (!isFormValid()) {
      return;
    }

    const rateDetails = car.rateDetails || {};

    const checkoutData = {
      paymentType: car.paymentType,
      SIPP_Code: car.sipp_code,
      Base_Price_rate_table: car.base_rate || 0,
      Currency: car.currency,
      length_of_rental: car.lor,
      Pickup_Location: formData.pickup.pickup_location,
      Pickup_airport_code: formData.pickup.pickup_airport_code,
      Pickup_city: formData.pickup.pickup_city,
      Pickup_country: formData.pickup.pickup_country,
      Pickup_airport_name: formData.pickup.pickup_airport_name,
      DropOff_Location: formData.dropoff.dropoff_location,
      DropOff_airport_code: formData.dropoff.dropoff_airport_code,
      DropOff_city: formData.dropoff.dropoff_city,
      DropOff_country: formData.dropoff.dropoff_country,
      DropOff_airport_name: formData.dropoff.dropoff_airport_name,
      Pickup_Date_Time: formData.pickup.pickup_date,
      DropOff_Date_Time: formData.dropoff.dropoff_date,
      Vehicle_Type: car.carType,
      Vehicle_Sub_Type: car.vehicleSubType,
      Seats: car.seats,
      Bags: car.bags,
      Doors: car.door,
      AC: car.ac,
      Mileage: car.mileage,
      Total_Price: car.total_price,
      Base_Price: car.base_rate || 0,
      Tax_on_Car_Price_Value: rateDetails.total_taxes || 0,
      Tax_on_Car_Price_Details_Breakup: rateDetails.taxes_detail || {},
      Extras_Total_Price: 0,
      Vehicle_Image: car.image,
      Vehicle_Name: `${car.name} ${car.model}`,
      Vehicle_Description: car.description,
      base_tax_charge: rateDetails.taxes_detail?.base_tax_charge || {},
      calculated_tax_charge:
        rateDetails.taxes_detail?.calculated_tax_charge || {},
      independent_tax_charge:
        rateDetails.taxes_detail?.independent_tax_charge || {},
    };

    navigate("/checkout", {
      state: { vehicleData: checkoutData, searchData: formData },
    });
  };

  const resetFilters = useCallback(() => {
    setFilters({
      vehicleFilters: {
        cars: { checked: false, subTypes: [] },
        suvs: { checked: false, subTypes: [] },
        "vans-trucks": { checked: false, subTypes: [] },
      },
      mileage: [],
      passengerFilters: {
        "4+": { checked: false, vehicleTypes: [] },
        "5+": { checked: false, vehicleTypes: [] },
        "6+": { checked: false, vehicleTypes: [] },
        "7+": { checked: false, vehicleTypes: [] },
      },
      maxPrice: "",
    });
    // setSortOption("");
    setSortOption("low-to-high");
  }, []);

  const groupCarsBySipp = (data) => {
    const result = {};

    data.forEach((car) => {
      const sipp = car.sipp_code;
      if (!result[sipp]) {
        result[sipp] = {
          sipp_code: sipp,
          ...car,
          providers: {},
        };
      }
      result[sipp].providers[car.paymentType] = {
        ...car,
      };
    });

    return Object.values(result);
  };

  const cars = groupCarsBySipp(filteredData);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="mt-24 text-center ">
          <img
            src={logo}
            alt="Loading..."
            className="mx-auto w-48 h-48 animate-spin"
          />
          <p className="mt-2 text-[#353535] text-xl font-lg">
            Loading vehicles...
          </p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-24 text-center">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-[#ca8032] text-white px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      // <div className="mt-24 text-center ">
      //   <h2 className="text-2xl font-bold text-[#006039] mb-2">Session Expired</h2>
      //   <p className="text-lg text-[#006039]">Your session has expired due to inactivity.</p>
      //   <button
      //     onClick={() => navigate("/")}
      //     className="mt-4 bg-[#ca8032] text-white px-4 py-2 rounded"
      //   >
      //     Back to Home
      //   </button>
      // </div>
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Session Expired
          </h2>
          <p className="text-gray-600 mb-4">
            Your session has expired due to inactivity.
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-[#ca8032] text-white px-6 py-2 rounded-lg hover:bg-orange-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-black bg-opacity-50 absolute inset-0"></div>
          <div className="bg-white text-black px-6 py-4 rounded-lg shadow-lg z-10 text-center max-w-sm mx-auto">
            <p className="text-lg font-medium">{popupMessage}</p>
          </div>
        </div>
      )}

      {!sessionExpired && selectedCar && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center px-4 py-10">
            <div className="bg-white/95 rounded-3xl p-8 max-w-5xl w-full shadow-2xl relative animate-fadeIn border border-gray-100">
              <button
                onClick={() => setSelectedCar(false)}
                className="absolute right-5 top-5 text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full shadow-sm transition"
              >
                ✕
              </button>

              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
                  Compare & Choose Your Plan
                </h2>
                <p className="text-gray-500 text-sm">
                  Select the best payment option for your preferred car rental
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-10">
                <div className="w-full lg:w-1/3 bg-gradient-to-b from-gray-50 to-gray-100 rounded-2xl p-6 shadow-inner relative border border-gray-100 hover:shadow-lg transition">
                  <div className="relative">
                    <img
                      src={selectedCar.image}
                      alt={selectedCar.description}
                      className="w-full h-44 object-contain mb-5"
                    />
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 text-center">
                    {selectedCar.description}
                  </h3>
                  <p className="text-gray-500 italic text-center mb-5">
                    or similar vehicle
                  </p>

                  <div className="flex flex-wrap justify-center gap-3 text-sm text-gray-600">
                    {selectedCar.seats && (
                      <div className="flex items-center gap-1 bg-white rounded-lg px-3 py-1 shadow-sm border">
                        <FaUserFriends className="text-[#ca8032]" />
                        <span>{selectedCar.seats} Seats</span>
                      </div>
                    )}

                    {selectedCar.bags && (
                      <div className="flex items-center gap-1 bg-white rounded-lg px-3 py-1 shadow-sm border">
                        <FaSuitcaseRolling className="text-[#ca8032]" />
                        <span>{selectedCar.bags} Bags</span>
                      </div>
                    )}
                    {selectedCar.door && (
                      <div className="flex items-center gap-1 bg-white rounded-lg px-3 py-1 shadow-sm border">
                        <GiCarDoor className="text-[#ca8032]" />
                        <span>{selectedCar.door} Doors</span>
                      </div>
                    )}
                    {selectedCar.ac && (
                      <div className="flex items-center gap-1 bg-white rounded-lg px-3 py-1 shadow-sm border">
                        <IoSnowOutline className="text-[#ca8032]" />
                        <span>AC</span>
                      </div>
                    )}
                  </div>

                  <hr className="my-5 border-gray-200" />

                  <div className="mt-5 flex items-center justify-center gap-2 text-xs text-gray-500 italic">
                    <span>Service Provided by</span>
                    <img
                      src={aombre}
                      alt="Aombre Logo"
                      className="h-5 object-contain opacity-90 hover:opacity-100 transition"
                    />
                  </div>
                </div>

                <div className="w-full lg:w-2/3 space-y-6">
                  {selectedCar.providers.PP && (
                    <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-white via-[#FFF8F1] to-[#FFEBD3] border border-[#ca8032]/40 shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
                      <div className="absolute top-0 right-0 bg-[#ca8032] text-white text-xs px-3 py-1 rounded-bl-2xl font-medium">
                        Best Value
                      </div>

                      <h4 className="text-lg font-semibold text-gray-800 mb-2">
                        Pay Now & Save More
                      </h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Get the best deal by paying upfront and locking in your
                        price.
                      </p>

                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {selectedCar.providers.PP.currency}
                            {selectedCar.providers.PP.price.toFixed(2)}
                            <span className="ml-1 text-base font-medium text-gray-500">
                              /day
                            </span>
                          </p>

                          <div className="flex items-center gap-1.5 mt-1">
                            <p className="text-sm text-gray-500">Total</p>
                            <span className="text-sm font-medium text-[#ca8032]">
                              {selectedCar.providers.PP.currency}{" "}
                              {selectedCar.providers.PP.total_price.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() =>
                            handleBookNow(selectedCar.providers.PP)
                          }
                          className="w-full sm:w-auto bg-gradient-to-r from-[#ca8032] to-[#E89C48] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transform transition"
                        >
                          Pay Now
                        </button>
                      </div>

                      <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                        <span className="text-green-600">
                          {" "}
                          Includes all taxes & fees
                        </span>
                      </div>
                    </div>
                  )}

                  {selectedCar.providers.POA && (
                    <div className="relative overflow-hidden rounded-2xl p-6 bg-white border border-gray-200 shadow-sm hover:border-[#ca8032]/70 hover:shadow-lg transition transform hover:-translate-y-1">
                      <div className="absolute top-0 right-0 bg-gray-700 text-white text-xs px-3 py-1 rounded-bl-2xl font-medium">
                        Pay at Counter
                      </div>

                      <h4 className="text-lg font-semibold text-gray-800 mb-2">
                        Reserve Now, Pay Later
                      </h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Reserve your car now and pay safely when you pick it up.
                      </p>

                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {selectedCar.providers.POA.currency}
                            {selectedCar.providers.POA.price.toFixed(2)}
                            <span className="ml-1 text-base font-medium text-gray-500">
                              /day
                            </span>
                          </p>

                          <div className="flex items-center gap-1.5 mt-1">
                            <p className="text-sm text-gray-500">Total</p>
                            <span className="text-sm font-medium text-[#ca8032]">
                              {selectedCar.providers.POA.currency}{" "}
                              {selectedCar.providers.POA.total_price.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() =>
                            handleBookNow(selectedCar.providers.POA)
                          }
                          className="w-full sm:w-auto bg-white border-2 border-[#ca8032] text-[#ca8032] px-6 py-3 rounded-xl font-semibold hover:bg-[#ca8032] hover:text-white transition"
                        >
                          Pay Later
                        </button>
                      </div>

                      <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                        <span className="text-green-600">
                          {" "}
                          Includes all taxes & fees
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!sessionExpired && (
        <div className="mt-[68px] md:mt-[75px] mb-6">
          {isDesktop || mobileEditMode ? (
            <form
              onSubmit={handleSubmit}
              className="p-2 bg-[#F8EFE5] rounded shadow mb-2 flex flex-col lg:flex-row gap-4 border border-[#ca8032]"
            >
              <div className="flex flex-col lg:flex-row gap-4 w-full">
                <div className="w-full relative">
                  <label className="text-sm font-medium">Pickup Location</label>
                  <input
                    autoComplete="off"
                    type="text"
                    value={pickupQuery}
                    onChange={handlePickupInput}
                    className="border rounded w-full h-12 px-2 py-1"
                  />
                  {showPickupSuggestions && pickupSuggestions.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white border rounded shadow max-h-60 overflow-y-auto mt-1">
                      {pickupSuggestions.map((loc, idx) => (
                        <li
                          key={idx}
                          onClick={() => handleSelectPickup(loc)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {loc.city} - {loc.airport_name} ({loc.airport_code}),{" "}
                          {loc.country_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="w-full relative">
                  <label className="text-sm font-medium">
                    Dropoff Location
                  </label>
                  <input
                    autoComplete="off"
                    type="text"
                    value={dropoffQuery}
                    onChange={handleDropoffInput}
                    className="border rounded w-full h-12 px-2 py-1"
                  />
                  {showDropoffSuggestions && dropoffSuggestions.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white border rounded shadow max-h-60 overflow-y-auto mt-1">
                      {dropoffSuggestions.map((loc, idx) => (
                        <li
                          key={idx}
                          onClick={() => handleSelectDropoff(loc)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {loc.city} - {loc.airport_name} ({loc.airport_code}),{" "}
                          {loc.country_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="w-full flex-1">
                  <label className="text-sm font-medium">Pickup Date</label>
                  <DatePicker
                    selected={pickupDate}
                    onChange={(date) => {
                      setPickupDate(date);
                      if (dropoffDate && date && dropoffDate <= date) {
                        setDropoffDate(null);
                        setDateTimeError(
                          "Dropoff date & time must be later than pickup."
                        );
                        triggerPopup(
                          "Dropoff date & time must be later than pickup."
                        );
                      } else {
                        setDateTimeError("");
                      }
                    }}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="border rounded w-[300px] h-12 px-2 py-1"
                    placeholderText="Select pickup date and time"
                    minDate={new Date()}
                  />
                </div>
                <div className="w-full flex-1">
                  <label className="text-sm font-medium">Dropoff Date</label>
                  <DatePicker
                    selected={dropoffDate}
                    onChange={(date) => {
                      if (pickupDate && date <= pickupDate) {
                        setDateTimeError(
                          "Dropoff date & time must be later than pickup."
                        );
                        triggerPopup(
                          "Dropoff date & time must be later than pickup."
                        );
                        setDropoffDate(null);
                        return;
                      }
                      setDropoffDate(date);
                      setDateTimeError("");
                    }}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="border rounded w-[300px] h-12 px-2 py-1"
                    placeholderText="Select dropoff date and time"
                    minDate={
                      pickupDate
                        ? new Date(
                          new Date(pickupDate).setDate(
                            new Date(pickupDate).getDate()
                          )
                        )
                        : new Date()
                    }
                  />
                </div>

                <div className="flex items-end">
                  <button
                    type="submit"
                    className="bg-[#ca8032] text-white p-3 rounded hover:bg-[#a06427] transition flex items-center justify-center h-12 w-12"
                    title="Update"
                  >
                    <FaSearch className="text-white w-5 h-5" />
                  </button>
                  {!isDesktop && (
                    <button
                      type="button"
                      onClick={() => setMobileEditMode(false)}
                      className="ml-2 bg-gray-300 text-gray-700 p-3 rounded hover:bg-gray-400 transition flex items-center justify-center h-12 w-12"
                      title="Cancel"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </form>
          ) : (
            <div className="p-2 bg-[#F8EFE5] rounded shadow mb-2 border border-[#ca8032]">
              <p>
                <span className="font-medium">Pickup Location:</span>{" "}
                {pickupQuery}
              </p>
              <p>
                <span className="font-medium">Dropoff Location:</span>{" "}
                {dropoffQuery}
              </p>
              <p>
                <span className="font-medium">Pickup Date:</span>{" "}
                {pickupDate ? pickupDate.toLocaleString() : ""}
              </p>
              <p>
                <span className="font-medium">Dropoff Date:</span>{" "}
                {dropoffDate ? dropoffDate.toLocaleString() : ""}
              </p>
              <button
                onClick={() => setMobileEditMode(true)}
                className="mt-2 bg-[#ca8032] text-white px-4 py-2 rounded hover:bg-[#a06427] transition"
              >
                Edit
              </button>
            </div>
          )}

          <div className="max-w-6xl mx-auto ">
            <div className=" max-w-6xl flex justify-between  items-center bg-white boreder  border-gray-500 rounded-lg p-3 mb-2 ">
              <h1 className="font-semibold text-xl">Choose a Vehicle Class</h1>
              <div>
                <select
                  onChange={(e) => setSortOption(e.target.value)}
                  className="border border-[#ca8032] px-3 py-2 rounded hidden lg:block"
                >
                  <option value="">Sort</option>
                  <option value="low-to-high">Price: Low to High</option>
                  <option value="high-to-low">Price: High to Low</option>
                  <option value="name-asc">Name: A → Z</option>
                  <option value="name-desc">Name: Z → A</option>
                </select>
              </div>
            </div>
            <div className="lg:hidden flex gap-2 mb-2 justify-between">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="flex items-center gap-1 bg-[#edebec] px-3 py-2 rounded"
              >
                <MdFilterList /> Filters
              </button>
              <select
                onChange={(e) => setSortOption(e.target.value)}
                className="border px-3 py-2 rounded"
              >
                <option value="">Sort</option>
                <option value="low-to-high">Price: Low to High</option>
                <option value="high-to-low">Price: High to Low</option>
                <option value="name-asc">Name: A → Z</option>
                <option value="name-desc">Name: Z → A</option>
              </select>
            </div>
            <div className="lg:flex gap-4  ">
              {(showMobileFilters || isDesktop) && (
                <div className="bg-white p-4 rounded-lg shadow-lg w-full lg:w-1/4 space-y-4">
                  <div className="flex items-center justify-between ">
                    <h1 className="font-semibold text-xl">Filters</h1>
                    <button
                      onClick={resetFilters}
                      className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 transition"
                    >
                      Clear Filters
                    </button>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">
                      Vehicle Types
                    </label>
                    <div className="space-y-2">
                      {Object.keys(filters.vehicleFilters).map((type) => (
                        <div key={type}>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={filters.vehicleFilters[type].checked}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  vehicleFilters: {
                                    ...prev.vehicleFilters,
                                    [type]: {
                                      ...prev.vehicleFilters[type],
                                      checked: e.target.checked,
                                      subTypes: e.target.checked
                                        ? prev.vehicleFilters[type].subTypes
                                        : [],
                                    },
                                  },
                                }))
                              }
                            />
                            <span className="capitalize">
                              {type === "vans-trucks" ? "Vans & Trucks" : type}
                            </span>
                          </label>
                          <div className="ml-6 mt-1 space-y-1">
                            {(
                              {
                                cars: [
                                  "Compact",
                                  "Intermediate",
                                  "Standard",
                                  "Special",
                                ],
                                suvs: [
                                  "Compact",
                                  "Intermediate",
                                  "Standard",
                                  "Fullsize",
                                  "Special",
                                ],
                                "vans-trucks": [
                                  "Mini Van",
                                  "Premium",
                                  "Standard",
                                  "Special",
                                ],
                              }[type] || []
                            ).map((subtype) => (
                              <label
                                key={subtype}
                                className="flex items-center space-x-2 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={filters.vehicleFilters[
                                    type
                                  ].subTypes.includes(subtype)}
                                  onChange={() => {
                                    setFilters((prev) => {
                                      const currentSubTypes =
                                        prev.vehicleFilters[type].subTypes;
                                      const newSubTypes =
                                        currentSubTypes.includes(subtype)
                                          ? currentSubTypes.filter(
                                            (s) => s !== subtype
                                          )
                                          : [...currentSubTypes, subtype];

                                      const isMainTypeChecked =
                                        newSubTypes.length > 0;

                                      return {
                                        ...prev,
                                        vehicleFilters: {
                                          ...prev.vehicleFilters,
                                          [type]: {
                                            ...prev.vehicleFilters[type],
                                            checked: isMainTypeChecked,
                                            subTypes: newSubTypes,
                                          },
                                        },
                                      };
                                    });
                                  }}
                                />
                                <span>{subtype}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Mileage</label>
                    <div className="space-y-1">
                      {["Unlimited", "Limited"].map((mile) => (
                        <label
                          key={mile}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={filters.mileage?.includes(mile)}
                            onChange={() => {
                              setFilters((prev) => {
                                const mileage = prev.mileage || [];
                                return {
                                  ...prev,
                                  mileage: mileage.includes(mile)
                                    ? mileage.filter((m) => m !== mile)
                                    : [...mileage, mile],
                                };
                              });
                            }}
                          />
                          <span>{mile}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Passengers</label>
                    <div className="space-y-1">
                      {Object.keys(filters.passengerFilters).map(
                        (passenger) => (
                          <label
                            key={passenger}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="checkbox"
                              checked={
                                filters.passengerFilters[passenger].checked
                              }
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  passengerFilters: {
                                    ...prev.passengerFilters,
                                    [passenger]: {
                                      ...prev.passengerFilters[passenger],
                                      checked: e.target.checked,
                                    },
                                  },
                                }))
                              }
                            />
                            <span>{passenger}</span>
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="w-full lg:flex-1 space-y-4">
                {filteredData.length === 0 ? (
                  <div className="mt-24 text-center px-4">
                    <img
                      src={nodata}
                      alt="No results"
                      className="mx-auto w-60 h-60 object-contain"
                    />
                    <p className="text-lg font-medium text-[#006039]">
                      No vehicles found for your search criteria.
                    </p>
                    <p className="text-sm mt-2 text-gray-600">
                      Please try adjusting your search parameters.
                    </p>
                  </div>
                ) : (
                  cars.map((car, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden sm:m-0 m-4"
                    >
                      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 p-4">
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full">
                          <img
                            src={car.image}
                            alt={`${car.name} ${car.description}`}
                            className="w-full sm:w-32 h-32 object-contain rounded  p-2"
                          />

                          <div className="flex flex-col justify-between w-full">
                            <div>
                              <p className="text-lg sm:text-xl font-semibold text-[#1f1f1f] leading-tight">
                                {car.description}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                {car.name} {car.model} or similar
                              </p>
                            </div>

                            <div className="flex flex-wrap gap-x-4 gap-y-2 text-gray-600 text-sm mt-3">
                              <div className="flex items-center gap-1">
                                <FaUserFriends className="text-[#ca8032]" />{" "}
                                {car.seats}
                              </div>
                              <div className="flex items-center gap-1">
                                <FaSuitcaseRolling className="text-[#ca8032]" />{" "}
                                {car.bags}
                              </div>
                              <div className="flex items-center gap-1">
                                <IoSnowOutline className="text-[#ca8032]" />
                                {car.ac}
                              </div>
                              <div className="flex items-center gap-1">
                                <GiCarDoor className="text-[#ca8032]" />{" "}
                                {car.door}
                              </div>
                              <div className="flex items-center gap-1"></div>
                            </div>

                            <div className="flex items-center gap-2 mt-3">
                              <span className="text-xs sm:text-sm text-gray-500">
                                Service Provided by:
                              </span>
                              <img
                                src={aombre}
                                alt="Company Logo"
                                className="w-16 sm:w-20 h-8 object-contain"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="hidden lg:flex items-center justify-center opacity-20 h-24 w-32 flex-shrink-0">
                          <img
                            src={logo}
                            alt="Company Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>

                        <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between w-full lg:w-auto lg:mt-0 gap-2 border-t lg:border-none pt-3 lg:pt-0">
                          <div className="flex flex-col items-start lg:items-end">
                            <div className="inline-flex items-baseline gap-1 whitespace-nowrap">
                              <span className="text-lg sm:text-2xl font-bold text-[#1f1f1f]">
                                {car.currency} {Number(car.price).toFixed(2)}
                              </span>
                              <span className="text-xs sm:text-sm font-light text-gray-600">
                                /day
                              </span>
                            </div>

                            <p className="text-sm sm:text-md font-semibold text-[#ca8032]">
                              Total: {car.currency}
                              {car.total_price}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500">
                              Includes all taxes
                            </p>
                          </div>
                          {Object.keys(car.providers).length > 1 ? (
                            <button
                              onClick={() => handleChooseCar(car)}
                              className="bg-[#ca8032] text-white px-4 py-2 rounded-lg w-auto sm:w-32 text-sm sm:text-base hover:bg-[#a06427] transition"
                            >
                              Choose
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBookNow(car)}
                              className="bg-[#ca8032] text-white px-4 py-2 rounded-lg w-auto sm:w-32 text-sm sm:text-base hover:bg-[#a06427] transition"
                            >
                              Book Now
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SearchResults;
